import { isAnswerObservationsResult, type AnswerMap, type QuizDefinition, type QuizResult, type ScoreResult } from "@quiz/quiz-core";
import type { LeadResponse } from "../api/leadApi";
import type { ResolvedUiCopy } from "../ui/copy";
import { button, el } from "../utils";
import { getQuizPresentation } from "@quiz/quiz-definitions";
import { renderCampaignMedia } from "../components/CampaignMedia";
import { renderComicQuestUnlockedResultScreen } from "./ResultScreen";

export function renderSuccessScreen(
  quiz: QuizDefinition,
  result: QuizResult,
  response: LeadResponse,
  copy: ResolvedUiCopy,
  onRestart: () => void,
  onExternalCtaClick?: () => void,
  answers?: AnswerMap
): HTMLElement {
  const presentation = getQuizPresentation(quiz);
  if (presentation.comicQuest && presentation.completion?.mode === "telegram" && !isAnswerObservationsResult(result)) {
    return renderComicQuestUnlockedResultScreen({
      quiz,
      result: result as ScoreResult,
      answers: answers ?? {},
      copy,
      response,
      onExternalCtaClick
    });
  }
  if (presentation.completion?.mode === "telegram") {
    return renderTelegramSuccessScreen(quiz, response, copy, onExternalCtaClick);
  }

  const screen = el("main", "screen success-screen");
  const shell = el("section", "success-shell");
  const media = renderCampaignMedia(presentation.mascot?.success, "success-media");
  if (media) shell.append(media);
  shell.append(el("p", "eyebrow", response.delivery_state === "failed" ? copy.leadSavedLocal : copy.leadSaved));
  shell.append(el("h1", "success-title", copy.fullResultReady));
  if (isAnswerObservationsResult(result)) {
    shell.append(el("h2", "success-result", quiz.personalizedResult?.unlockedTitle ?? copy.fullResultTitle));
    shell.append(el("p", "success-text", quiz.personalizedResult?.outro ?? copy.fullResultReady));
  } else {
    const definition = quiz.results.find((candidate) => candidate.id === result.id)!;
    shell.append(el("h2", "success-result", definition.title));
    shell.append(el("p", "success-text", definition.gatedContent));
  }
  if (response.delivery_state === "failed") {
    shell.append(el("p", "form-error", copy.leadSavedLocal));
  }
  shell.append(button("secondary-button", copy.restart, onRestart));
  screen.append(shell);
  return screen;
}

function renderTelegramSuccessScreen(
  quiz: QuizDefinition,
  response: LeadResponse,
  copy: ResolvedUiCopy,
  onExternalCtaClick?: () => void
): HTMLElement {
  const completion = getQuizPresentation(quiz).completion!;
  const screen = el("main", "screen success-screen success-screen--telegram");
  const shell = el("section", "success-shell success-shell--telegram");
  shell.append(el("p", "eyebrow", response.delivery_state === "failed" ? copy.leadSavedLocal : copy.leadSaved));
  shell.append(el("h1", "success-title", completion.successTitle));
  shell.append(el("p", "success-text", completion.successText));
  if (response.delivery_state === "failed") {
    shell.append(el("p", "form-error", copy.leadSavedLocal));
  }
  const cta = el("a", "primary-button telegram-plan-cta", completion.ctaLabel) as HTMLAnchorElement;
  cta.href = completion.ctaUrl;
  cta.target = "_blank";
  cta.rel = "noopener noreferrer";
  cta.addEventListener("click", () => onExternalCtaClick?.());
  shell.append(cta);
  screen.append(shell);
  return screen;
}
